import AsyncSelect from 'react-select/async';
import { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { CustomReactSelectStyles } from 'pages/Promotions/styles/customStyles';
import {
  getSearchProductsFromES,
  ProductESSearchParams,
} from 'services/productService';

interface OptionType {
  value: string;
  label: string;
}

type ProductSearchESFieldType = 'name' | 'item_id_number' | 'item_code';

const FIELD_INDEX: Record<ProductSearchESFieldType, number> = {
  name: 0,
  item_id_number: 1,
  item_code: 2,
};

interface AutoCompleteProps {
  id: string;
  name: string;
  value: string;
  selectedOption?: OptionType | null;
  onChange?: (option: OptionType | null) => void;
  onOptionSelect?: (option: OptionType | null, totalResultIds: string[]) => void;
  onInputValueChange?: (value: string) => void;
  onBlur: () => void;
  isDarkMode: boolean;
  searchType?: ProductSearchESFieldType;
  allValues?: ProductESSearchParams;
  staticOptions?: OptionType[];
  minSearchLength?: number;
  placeholder?: string;
}

const AutoComplete = ({
  id,
  name,
  value,
  selectedOption,
  onChange,
  onOptionSelect,
  onInputValueChange,
  onBlur,
  isDarkMode,
  searchType,
  allValues,
  staticOptions,
  minSearchLength = 3,
  placeholder,
}: AutoCompleteProps) => {
  const latestRequestRef = useRef(0);

  const lastOptionsRef = useRef<OptionType[]>([]);
  const totalResultsRef = useRef<string[]>([]);
  const skipNextBlurRef = useRef(false);
  const latestSelectionRef = useRef(0);
  const isStaticMode = Array.isArray(staticOptions);
  const externalSelectedOption = useMemo(
    () => selectedOption ?? (value ? { value, label: value } : null),
    [selectedOption, value]
  );

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectRenderKey, setSelectRenderKey] = useState(0);
  const [pendingSelectedOption, setPendingSelectedOption] =
    useState<OptionType | null>(null);
  const selectedOptionValue = pendingSelectedOption ?? externalSelectedOption;

  // Keep a ref so the debounced function always reads latest filters
  // without needing them in useMemo deps (which would recreate/cancel the debounce)
  const allValuesRef = useRef(allValues);
  useEffect(() => {
    allValuesRef.current = allValues;
  }, [allValues]);

  const debouncedFetcher = useMemo(() => {
    const fn = debounce(
      async (keyword: string, callback: (options: OptionType[]) => void) => {
        const requestId = ++latestRequestRef.current;

        try {
          if (!searchType || !allValuesRef.current) {
            callback([]);
            return;
          }

          const params: ProductESSearchParams = {
            ...allValuesRef.current,
            [searchType]: keyword,
          };

          const res = await getSearchProductsFromES(params);

          if (requestId !== latestRequestRef.current) return;

          totalResultsRef.current = res.data?.total_results?.list ?? [];

          const fieldIdx = FIELD_INDEX[searchType];
          const seen = new Set<string>();
          const apiOptions: OptionType[] = (res.data?.limit_results?.list ?? [])
            .map((item: string) => item.split('|')[fieldIdx] || '')
            .filter((v: string) => {
              if (!v || seen.has(v)) return false;
              seen.add(v);
              return true;
            })
            .map((v: string) => ({ value: v, label: v }));

          let finalOptions: OptionType[] = [];

          if (apiOptions.length === 0) {
            finalOptions = [
              {
                value: keyword,
                label: keyword,
              },
            ];
          } else {
            const exactMatch = apiOptions.some(
              (opt) => opt.value.toLowerCase() === keyword.toLowerCase()
            );

            if (!exactMatch) {
              finalOptions = [
                {
                  value: keyword,
                  label: keyword,
                },
                ...apiOptions,
              ];
            } else {
              finalOptions = apiOptions;
            }
          }

          lastOptionsRef.current = finalOptions;

          callback(finalOptions);
        } catch (err) {
          console.error(err);

          const fallbackOption = [
            {
              value: keyword,
              label: keyword,
            },
          ];

          lastOptionsRef.current = fallbackOption;

          callback(fallbackOption);
        }
      },
      400
    );

    return fn;
  }, [searchType]);

  useEffect(() => {
    return () => {
      debouncedFetcher.cancel();
    };
  }, [debouncedFetcher]);

  const loadOptions = useCallback(
    (keyword: string, callback: (options: OptionType[]) => void): void => {
      if (isStaticMode) {
        const normalizedKeyword = keyword.trim().toLowerCase();
        const baseOptions = staticOptions ?? [];
        const filteredOptions = normalizedKeyword
          ? baseOptions.filter((option) =>
              option.label.toLowerCase().includes(normalizedKeyword)
            )
          : baseOptions;

        lastOptionsRef.current = filteredOptions;
        callback(filteredOptions);
        return;
      }

      if (!keyword) {
        callback(lastOptionsRef.current);
        return;
      }

      if (keyword.length < minSearchLength) {
        callback([]);
        return;
      }

      debouncedFetcher(keyword, callback);
    },
    [debouncedFetcher, isStaticMode, minSearchLength, staticOptions]
  );

  const resolveSelectedOptionIds = useCallback(
    async (option: OptionType | null) => {
      if (!option || !searchType || !allValuesRef.current) {
        return totalResultsRef.current;
      }

      try {
        const response = await getSearchProductsFromES({
          ...allValuesRef.current,
          [searchType]: option.value,
        });

        return response.data?.total_results?.list ?? [];
      } catch (error) {
        console.error('Failed to resolve selected autocomplete option:', error);
        return totalResultsRef.current;
      }
    },
    [searchType]
  );

  useEffect(() => {
    setPendingSelectedOption((current) => {
      if (!current) return current;
      if (!externalSelectedOption) {
        return null;
      }
      if (current.value === externalSelectedOption.value) {
        return null;
      }
      return current;
    });
  }, [externalSelectedOption]);

  useEffect(() => {
    if (isStaticMode) {
      lastOptionsRef.current = staticOptions ?? [];
    }
  }, [isStaticMode, staticOptions]);

  return (
    <AsyncSelect<OptionType, false>
      key={`${id}-${selectRenderKey}`}
      id={id}
      name={name}
      value={selectedOptionValue}
      onInputChange={(newValue, { action }) => {
        if (action === 'input-change') {
          latestSelectionRef.current += 1;
          skipNextBlurRef.current = false;
          setPendingSelectedOption(null);
          onInputValueChange?.(newValue);
        }
      }}
      onChange={async (option) => {
        const selectionId = ++latestSelectionRef.current;

        skipNextBlurRef.current = true;
        setPendingSelectedOption(option);
        setIsMenuOpen(false);
        setSelectRenderKey((current) => current + 1);

        if (onOptionSelect) {
          const resolvedIds = await resolveSelectedOptionIds(option);
          if (selectionId !== latestSelectionRef.current) return;
          onOptionSelect(option, resolvedIds);
        } else {
          if (selectionId !== latestSelectionRef.current) return;
          onChange?.(option);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && !isMenuOpen) {
          event.preventDefault();
        }
      }}
      onMenuOpen={() => {
        setIsMenuOpen(true);
      }}
      onMenuClose={() => {
        setIsMenuOpen(false);
      }}
      onBlur={() => {
        setIsMenuOpen(false);
        window.setTimeout(() => {
          if (skipNextBlurRef.current) {
            skipNextBlurRef.current = false;
            return;
          }
          onBlur();
        }, 0);
      }}
      cacheOptions
      defaultOptions={lastOptionsRef.current}
      loadOptions={loadOptions}
      placeholder={placeholder}
      isClearable
      noOptionsMessage={() => null}
      openMenuOnFocus
      styles={CustomReactSelectStyles(isDarkMode)}
    />
  );
};

export default AutoComplete;
